import type { TFunction } from 'i18next'
import type { ChatPresenceStatus, ChatShownStatus } from '@/lib/sdk-official/resources/chat'

/**
 * F-184 "visto por último", WhatsApp style (David): today at 14:32, yesterday at 09:10, a weekday
 * this week, a date before that. `short` is the narrow window header ("visto hoje às 14:32").
 * Null when the API sent no time (the caller says "offline").
 */
export const lastSeenLabel = (t: TFunction, language: string, iso: string | null | undefined, form: 'full' | 'short' = 'full', now: Date = new Date()): string | null => {
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  const time = new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit' }).format(at)
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((startOfDay(now) - startOfDay(at)) / 86400000)
  const when = days <= 0
    ? t('lastSeen.today', { ns: 'chat', time })
    : days === 1
      ? t('lastSeen.yesterday', { ns: 'chat', time })
      : days < 7
        ? t('lastSeen.onDay', { ns: 'chat', time, day: new Intl.DateTimeFormat(language, { weekday: 'short' }).format(at) })
        : t('lastSeen.onDate', { ns: 'chat', time, date: new Intl.DateTimeFormat(language, { day: '2-digit', month: '2-digit' }).format(at) })
  return t(form === 'short' ? 'lastSeen.short' : 'lastSeen.full', { ns: 'chat', when })
}

/** F-184: the dot colors. Green Disponível, yellow Ausente (automatic), red Ocupado, gray Indisponível. */
export const MY_STATUS_DOT: Record<ChatShownStatus, string> = {
  AVAILABLE: 'bg-green-500',
  AWAY: 'bg-amber-400',
  BUSY: 'bg-red-500',
  UNAVAILABLE: 'bg-gray-400',
}

export const MY_STATUS_KEY: Record<ChatShownStatus, string> = {
  AVAILABLE: 'status.available',
  AWAY: 'status.away',
  BUSY: 'status.busy',
  UNAVAILABLE: 'status.unavailable',
}

/** F-184: no mouse, keyboard or visible tab for this long turns Disponível into Ausente (never Ocupado or Indisponível). */
export const AWAY_AFTER_MS = 10 * 60 * 1000

/** Someone else's dot: red while online and Ocupado, green while online, nothing (or gray where a row always shows one) offline. */
export const presenceDotClass = (online: boolean | null | undefined, status: ChatPresenceStatus | null | undefined): string | null =>
  !online ? null : status === 'BUSY' ? 'bg-red-500' : status === 'AWAY' ? 'bg-amber-400' : 'bg-green-500'

export const isBusy = (online: boolean | null | undefined, status: ChatPresenceStatus | null | undefined): boolean =>
  Boolean(online) && status === 'BUSY'
